import { Box, Divider, Paper, styled, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import { RecentCallsTracker } from './RecentCallsTracker';

import { isDevOrQaEnvironment } from 'common/utils';
import { useWebRTCLiveStats } from 'features/analytics/hooks/';
import { useAppSelector } from 'features/app/hooks';
import { TerminationType } from 'features/call/call-base/enums';
import { sipCallIdSelector } from 'features/call/call-base/store/selectors';
import { selectCallPageStatus } from 'features/call/call-ui-state/store';
import { trainingForkerSelector } from 'features/training-forker/store';
import {
  selectCallerSessionId,
  selectCallerSessionStatus,
  selectSwitchboardConnections,
  selectWebRtcRemoteIdentities,
} from 'features/caller-session/store';
import { RtcSessionTracker } from 'features/sip/services';
import type { SourceMediaTypes } from 'features/source-media-tracks/enums';
import {
  SourceMediaTracks,
  type SourceMediaTrackDiagnostics,
} from 'features/source-media-tracks/services';
import theme from 'features/theme/theme';
import { selectVideoRenderingStatus } from 'features/remote-media/store';

const MENU_KEY = '*614';
const debugMenuVisible = Boolean(import.meta.env.VITE_APP_DEBUG_MENU_VISIBLE);

type MediaTracksSnapshot = Partial<
  Record<SourceMediaTypes, SourceMediaTrackDiagnostics>
>;

export const HiddenDebugMenu = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [typedKeys, setTypedKeys] = useState('');
  const [mediaTrackDiagnostics, setMediaTrackDiagnostics] =
    useState<MediaTracksSnapshot>(() => {
      try {
        return SourceMediaTracks.getInstance().getTrackDiagnostics();
      } catch (error) {
        console.warn(
          'Unable to initialize SourceMediaTracks diagnostics',
          error
        );
        return {};
      }
    });

  const callerSessionStatus = useAppSelector(selectCallerSessionStatus);
  const callerSessionId = useAppSelector(selectCallerSessionId);
  const trainingForkerState = useAppSelector(trainingForkerSelector);
  const sipCallId = useAppSelector(sipCallIdSelector);
  const isDevOrQa = isDevOrQaEnvironment;
  const webRtcConnections = useAppSelector(selectWebRtcRemoteIdentities);
  const switchboardConnections = useAppSelector(selectSwitchboardConnections);
  const callPageStatus = useAppSelector(selectCallPageStatus);
  const { connectionStats, isStatsAvailable } = useWebRTCLiveStats(isVisible);

  const callLog = useMemo(() => {
    return RecentCallsTracker.getInstance().getCallLog();
  }, []);

  const mediaTrackEntries = useMemo(() => {
    return Object.entries(mediaTrackDiagnostics).map(
      ([sourceMediaType, diagnostics]) => ({
        sourceMediaType: sourceMediaType as SourceMediaTypes,
        diagnostics,
      })
    );
  }, [mediaTrackDiagnostics]);

  // Get session URIs from RtcSessionTracker
  const directSession = RtcSessionTracker.getDirectSession();
  const teamingSession = RtcSessionTracker.getTeamingSession();
  const wavelloSession = RtcSessionTracker.getWavelloSession();
  const directSessionUri =
    directSession?.remote_identity?.uri?.toString() || 'None';
  const teamingSessionUri =
    teamingSession?.remote_identity?.uri?.toString() || 'None';
  const wavelloSessionUri =
    wavelloSession?.remote_identity?.uri?.toString() || 'None';

  const videoDetectionStatus = useAppSelector(selectVideoRenderingStatus);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      setTypedKeys((prev) => (prev + event.key).slice(-MENU_KEY.length));
    };
    if (debugMenuVisible) {
      setIsVisible(true);
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    try {
      const sourceMediaTracks = SourceMediaTracks.getInstance();

      const handleTracksChanged = () => {
        setMediaTrackDiagnostics(sourceMediaTracks.getTrackDiagnostics());
      };

      handleTracksChanged();
      sourceMediaTracks.addStreamsChangedListener(handleTracksChanged);

      return () => {
        sourceMediaTracks.removeStreamsChangedListener(handleTracksChanged);
      };
    } catch (error) {
      console.warn(
        'Unable to subscribe to SourceMediaTracks diagnostics',
        error
      );
    }
  }, []);

  useEffect(() => {
    if (typedKeys === MENU_KEY) {
      setIsVisible((prev) => !prev);
      setTypedKeys('');
    }
  }, [typedKeys]);

  if (!isDevOrQa || !isVisible) {
    return null;
  }

  return (
    <DebugContainer elevation={4}>
      <Section>
        <SectionTitle variant='subtitle2'>Session Information</SectionTitle>
        <StatusItem>
          Session Status: <StatusValue>{callerSessionStatus}</StatusValue>
        </StatusItem>
        <StatusItem>
          Call Page Status: <StatusValue>{callPageStatus}</StatusValue>
        </StatusItem>
        <StatusItem>
          Caller Session ID: <StatusValue>{callerSessionId}</StatusValue>
        </StatusItem>
        <StatusItem>
          SIP Call ID: <StatusValue>{sipCallId}</StatusValue>
        </StatusItem>
        <StatusItem>
          Video Rendering Status:
          <StatusValue>{videoDetectionStatus ?? 'Unknown'}</StatusValue>
        </StatusItem>
      </Section>

      <StyledDivider />

      <Section>
        <SectionTitle variant='subtitle2'>Connection Status</SectionTitle>
        <StatusItem>
          WebRTC Connections:{' '}
          <StatusValue>{webRtcConnections?.length || 0}</StatusValue>
        </StatusItem>
        <StatusItem>
          Switchboard:{' '}
          <StatusValue>{switchboardConnections?.length || 0}</StatusValue>
        </StatusItem>
        <StatusItem>
          Direct Session URI: <StatusValue>{directSessionUri}</StatusValue>
        </StatusItem>
        <StatusItem>
          Teaming Session URI: <StatusValue>{teamingSessionUri}</StatusValue>
        </StatusItem>
        <StatusItem>
          Wavello Session URI: <StatusValue>{wavelloSessionUri}</StatusValue>
        </StatusItem>
      </Section>

      <StyledDivider />

      <Section>
        <SectionTitle variant='subtitle2'>Source Media Tracks</SectionTitle>
        {mediaTrackEntries.length === 0 && (
          <StatusItem>
            Snapshot: <StatusValue>No track data available</StatusValue>
          </StatusItem>
        )}
        {mediaTrackEntries.map(({ sourceMediaType, diagnostics }) => {
          if (!diagnostics) {
            return null;
          }

          const trackDetails = diagnostics.trackDetails;
          const availability = (() => {
            if (diagnostics.hasTrack) {
              return 'Attached';
            }
            if (diagnostics.isTrackUnavailable) {
              return 'Unavailable';
            }
            return 'Pending';
          })();

          return (
            <Box key={sourceMediaType} mb={2}>
              <ConnectionTitle variant='body2'>
                {sourceMediaType}
              </ConnectionTitle>
              <StatusItem>
                Scope:{' '}
                <StatusValue>
                  {diagnostics.isLocalType ? 'Local' : 'Remote'}
                </StatusValue>
              </StatusItem>
              <StatusItem>
                Availability: <StatusValue>{availability}</StatusValue>
              </StatusItem>
              <StatusItem>
                Device ID:{' '}
                <StatusValue>{diagnostics.deviceId ?? 'None'}</StatusValue>
              </StatusItem>
              <StatusItem>
                Monitored ID:{' '}
                <StatusValue>
                  {diagnostics.monitoredTrackId ?? 'None'}
                </StatusValue>
              </StatusItem>
              <StatusItem>
                Track Label:{' '}
                <StatusValue>{trackDetails?.label ?? 'N/A'}</StatusValue>
              </StatusItem>
              <StatusItem>
                Track ID: <StatusValue>{trackDetails?.id ?? 'N/A'}</StatusValue>
              </StatusItem>
              <StatusItem>
                Track Kind:{' '}
                <StatusValue>{trackDetails?.kind ?? 'N/A'}</StatusValue>
              </StatusItem>
              <StatusItem>
                Ready State:{' '}
                <StatusValue>{trackDetails?.readyState ?? 'N/A'}</StatusValue>
              </StatusItem>
              <StatusItem>
                Enabled:{' '}
                <StatusValue>
                  {(trackDetails?.enabled ?? false) ? 'Yes' : 'No'}
                </StatusValue>
              </StatusItem>
              <StatusItem>
                Muted:{' '}
                <StatusValue>
                  {(trackDetails?.muted ?? false) ? 'Yes' : 'No'}
                </StatusValue>
              </StatusItem>
            </Box>
          );
        })}
      </Section>

      <StyledDivider />

      <Section>
        <SectionTitle variant='subtitle2'>Training Forker</SectionTitle>
        <StatusItem>
          Forker Enabled:{' '}
          <StatusValue>
            {trainingForkerState.enabled ? 'Yes' : 'No'}
          </StatusValue>
        </StatusItem>
        <StatusItem>
          Forker Active:{' '}
          <StatusValue>{trainingForkerState.active ? 'Yes' : 'No'}</StatusValue>
        </StatusItem>
      </Section>

      {isStatsAvailable && (
        <>
          <StyledDivider />
          <Section>
            <SectionTitle variant='subtitle2'>
              Connection Stats (Live)
            </SectionTitle>
            {Object.entries(connectionStats).map(([peerId, stats], index) => {
              const shortPeerId = peerId.split('@')[0] || `Peer ${index + 1}`;

              if (!stats) {
                return (
                  <Box key={peerId} mb={2}>
                    <ConnectionTitle variant='body2'>
                      {shortPeerId}
                    </ConnectionTitle>
                    <StatusItem>
                      Status: <StatusValue>No data available</StatusValue>
                    </StatusItem>
                  </Box>
                );
              }

              return (
                <Box key={peerId} mb={2}>
                  <ConnectionTitle variant='body2'>
                    {shortPeerId}
                  </ConnectionTitle>
                  <StatusItem>
                    Video FPS (In/Out):{' '}
                    <StatusValue>
                      {Math.round(stats.inboundVideoFps * 10) / 10} /{' '}
                      {Math.round(stats.outboundVideoFps * 10) / 10}
                    </StatusValue>
                  </StatusItem>
                  <StatusItem>
                    Video Jitter:{' '}
                    <StatusValue>
                      {Math.round(stats.videoJitter * 1000) / 1000}ms
                    </StatusValue>
                  </StatusItem>
                  <StatusItem>
                    Audio Jitter:{' '}
                    <StatusValue>
                      {Math.round(stats.audioJitter * 1000) / 1000}ms
                    </StatusValue>
                  </StatusItem>
                  <StatusItem>
                    Round Trip Time:{' '}
                    <StatusValue>
                      {Math.round(stats.roundTripTime)}ms
                    </StatusValue>
                  </StatusItem>
                  <StatusItem>
                    Packet Loss:{' '}
                    <StatusValue>
                      {Math.round(stats.packetLossRate * 100) / 100}%
                    </StatusValue>
                  </StatusItem>
                  <StatusItem>
                    Video Bitrate:{' '}
                    <StatusValue>{stats.videoBitrate} kbps</StatusValue>
                  </StatusItem>
                  <StatusItem>
                    Video Resolution (In/Out):{' '}
                    <StatusValue>
                      {stats.inboundVideoResolution} /{' '}
                      {stats.outboundVideoResolution}
                    </StatusValue>
                  </StatusItem>
                </Box>
              );
            })}
          </Section>
        </>
      )}

      <StyledDivider />

      <Section>
        <SectionTitle variant='subtitle2'>Recent Calls</SectionTitle>
        {callLog
          ?.slice()
          .reverse()
          .map((call, idx: number) => (
            <CallLogItem key={call.callId}>
              <CallId variant='body2'>
                #{idx + 1}: {call.callId}
              </CallId>
              <TerminationCode variant='body2'>
                {TerminationType[call.terminationCode]}
              </TerminationCode>
            </CallLogItem>
          ))}
      </Section>
    </DebugContainer>
  );
};

const DebugContainer = styled(Paper)(() => ({
  position: 'fixed',
  bottom: theme.spacing(2),
  right: theme.spacing(2),
  padding: theme.spacing(3),
  maxWidth: '600px',
  maxHeight: '80vh',
  overflowY: 'auto',
  zIndex: 9999,
  opacity: 0.97,
  backgroundColor: '#1a1a1a',
  color: '#fff',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
  '&:hover': {
    opacity: 1,
  },
}));

const Section = styled(Box)(() => ({
  marginBottom: theme.spacing(3),
}));

const SectionTitle = styled(Typography)(() => ({
  color: '#60a5fa',
  fontWeight: 700,
  marginBottom: theme.spacing(2),
  fontSize: '1.1rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
}));

const StatusValue = styled('strong')({
  color: '#4ade80',
  fontWeight: 600,
  fontFamily: 'monospace',
  wordBreak: 'break-all',
});

const StatusItem = styled(Typography)(() => ({
  display: 'grid',
  gridTemplateColumns: '180px 1fr',
  gap: theme.spacing(1),
  padding: theme.spacing(0.75, 1),
  color: '#e5e7eb',
  fontWeight: 500,
  marginBottom: theme.spacing(0.5),
}));

const CallId = styled(Typography)({
  color: '#4ade80',
  fontWeight: 600,
  fontFamily: 'monospace',
});

const TerminationCode = styled(Typography)({
  color: '#f87171',
  fontWeight: 600,
});

const CallLogItem = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  color: '#e5e7eb',
  fontWeight: 500,
  marginBottom: theme.spacing(0.5),
}));

const StyledDivider = styled(Divider)(() => ({
  margin: theme.spacing(2, 0),
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
}));

const ConnectionTitle = styled(Typography)({
  color: '#fbbf24',
  fontWeight: 600,
  fontFamily: 'monospace',
  marginBottom: theme.spacing(0.5),
});
